import React from 'react';
import {
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import CommonEnrollmentCard from 'components/group-info/CommonEnrollmentCard';
import { EnrollmentCardType, EnrollmentPeriod, EnrollmentStage, EnrollmentType } from 'types/enrollmentService';
import RenewalEnrollmentCard from 'components/group-info/enrollment-cards/RenewalEnrollmentCard';
import InstalledEnrollmentCard from 'components/group-info/enrollment-cards/InstalledEnrollmentCard';
import { observer } from 'mobx-react-lite';
import InstalledPortfolioCard from 'components/group-info/enrollment-cards/InstalledEnrollmentCard/InstalledPortfolioCard';
import { getMaxRateCount, hasMultiProductsPortfolio } from 'components/group-info/enrollment-cards/utils';
import { createStyles } from './styles';

interface Props {
  type: EnrollmentCardType;
  enrollmentPeriod: EnrollmentPeriod;
  onContinue?: () => void;
}

const EnrollmentCardWrapper: React.FC<Props> = observer(({ type, enrollmentPeriod, onContinue }) => {
  const classes = createStyles();

  // Only 1 proposal can be confirmed per enrollment period
  const proposalDetails = enrollmentPeriod.proposals
    .filter(({ confirmed }) => confirmed)
    .map((proposal) => (
      <li key={proposal.cartId}>
        {type !== 'ee' && (
          <div className={classes.proposalDetailsRecord}>
            <span className={classes.proposalDetailKey}>Proposal:</span>
            <span className={classes.productLineValue}>{proposal.name}</span>
          </div>
        )}
        <div className={classes.proposalDetailsRecord}>
          <span className={classes.proposalDetailKey}>Products: </span>
          <span className={classes.productLineValue}>
            {proposal.productLines
              .sort((a, b) => {
                return sortByProductLineId(a.id, b.id);
              })
              .filter((productLine) => productLine.confirmedPortfolioId != null) // display only enrolled product lines
              .map((productLine) => PRODUCT_LINES_CONTENT_MAP[productLine.id].label)
              .join(', ')}
          </span>
        </div>
      </li>
    ));

  const cardsList =
    !!enrollmentPeriod.installedProposal &&
    enrollmentPeriod.installedProposal.productLines
      .sort((a, b) => sortByProductLineId(a.id, b.id))
      .filter((productLineData) => productLineData.products.length > 0)
      .map((productLineData) => {
        return (
          <InstalledPortfolioCard
            key={`${enrollmentPeriod.id}-${productLineData.id}`}
            productLineData={productLineData}
            maxRatesCount={getMaxRateCount(
              enrollmentPeriod.installedProposal?.productLines || [],
              EnrollmentType.INSTALLED,
            )}
            hasMultiProductsPortfolio={hasMultiProductsPortfolio(enrollmentPeriod.installedProposal!)}
            effectiveDate={enrollmentPeriod.effectiveDate}
          />
        );
      });

  const buildFooter = (): JSX.Element | null => {
    if (enrollmentPeriod.hasConfirmedProposal) {
      return (
        <ul className={classes.proposalDetailsSection}>
          {/* only 1 proposal can be confirmed per enrollment period */}
          {proposalDetails}
        </ul>
      );
    }
    if (cardsList && cardsList.length > 0) {
      return <div className={classes.portfoliosList}>{cardsList}</div>;
    }
    return null;
  };

  return (
    <>
      {enrollmentPeriod.stage === EnrollmentStage.RENEWAL && (
        <RenewalEnrollmentCard
          enrollmentPeriod={enrollmentPeriod}
          onContinue={onContinue}
          footer={
            proposalDetails.length > 0 ? <ul className={classes.proposalDetailsSection}>{proposalDetails}</ul> : null
          }
        />
      )}
      {enrollmentPeriod.stage === EnrollmentStage.INSTALLED && (
        <InstalledEnrollmentCard enrollmentPeriod={enrollmentPeriod} />
      )}

      {/* Common Card */}
      {enrollmentPeriod.stage !== EnrollmentStage.RENEWAL && enrollmentPeriod.stage !== EnrollmentStage.INSTALLED && (
        <CommonEnrollmentCard
          enrollmentPeriod={enrollmentPeriod}
          onContinue={onContinue}
          type={type}
          footer={buildFooter()}
        />
      )}
    </>
  );
});

export default EnrollmentCardWrapper;
